import { UNIT_LABELS, type UnitType } from '@bienenkampf/shared';
import { Button, Card } from '@bienenkampf/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatAbsRel, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import type { HiveView } from './types.js';

/** Stationierte Unterstuetzung im eigenen Stock; der Gastgeber kann sie heimschicken. */
export function SupportCard({
  worldId,
  view,
  now,
}: {
  worldId: number;
  view: HiveView;
  now: Date;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const dismiss = useMutation(
    trpc.movement.dismissSupport.mutationOptions({
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId: view.id }) });
        void qc.invalidateQueries({ queryKey: trpc.movement.pathKey() });
      },
    }),
  );
  const support =
    (
      view as HiveView & {
        support?: Array<{
          movementId: number;
          ownerName: string;
          units: Record<string, number>;
          since: Date;
        }>;
      }
    ).support ?? [];
  if (support.length === 0) return null;
  return (
    <Card title={`Unterstützung (${support.length})`}>
      <ul className="flex flex-col gap-2 text-sm">
        {support.map((s) => (
          <li key={s.movementId} className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{s.ownerName}</span>
            <span className="text-xs text-stone-400">
              {Object.entries(s.units)
                .filter(([, n]) => n > 0)
                .map(([t, n]) => `${formatNumber(n)} ${UNIT_LABELS[t as UnitType]}`)
                .join(', ')}{' '}
              · seit {formatAbsRel(s.since, now)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              loading={dismiss.isPending}
              onClick={() => dismiss.mutate({ worldId, movementId: s.movementId })}
            >
              Heimschicken
            </Button>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-stone-500">
        Stationierte Bienen verteidigen diesen Stock mit und tragen Verluste anteilig.
      </p>
      {dismiss.error && <p className="mt-1 text-sm text-red-400">{dismiss.error.message}</p>}
    </Card>
  );
}
